import React, { useEffect, useMemo, useRef } from 'react';
import { Bike, CheckCircle2, Clock, ExternalLink, MapPin, MessageCircle, PackageCheck, Phone, Printer, Radio, XCircle } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/Button';
import { DeliveryOrder, DeliveryOrderStatus } from '../types';
import { formatCurrency } from '../services/dataService';

const statusConfig: Record<DeliveryOrderStatus, { label: string; color: string; action?: string; next?: DeliveryOrderStatus }> = {
  RECEIVED: { label: 'Novo', color: 'bg-amber-100 text-amber-700 border-amber-200', action: 'Aceitar pedido', next: 'ACCEPTED' },
  ACCEPTED: { label: 'Aceito', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  PREPARING: { label: 'Preparando', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  READY: { label: 'Pronto', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', action: 'Despachar', next: 'OUT_FOR_DELIVERY' },
  OUT_FOR_DELIVERY: { label: 'Saiu para entrega', color: 'bg-blue-100 text-blue-700 border-blue-200', action: 'Entregue', next: 'DELIVERED' },
  DELIVERED: { label: 'Entregue', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  CANCELED: { label: 'Cancelado', color: 'bg-red-100 text-red-700 border-red-200' },
};

const getAddress = (order: DeliveryOrder) => {
  const customer = order.customer;
  return `${customer.address}, ${customer.number} - ${customer.neighborhood}${customer.complement ? `, ${customer.complement}` : ''}`;
};

export const DeliveryPanel = () => {
  const { currentCompany, deliveryOrders, updateDeliveryOrderStatus, receiveDeliveryPayment } = useStore();
  const lastNewCount = useRef(0);

  const grouped = useMemo(() => {
    const active = deliveryOrders.filter(order => !['DELIVERED', 'CANCELED'].includes(order.status));
    return {
      newOrders: active.filter(order => order.status === 'RECEIVED'),
      preparing: active.filter(order => ['ACCEPTED', 'PREPARING', 'READY'].includes(order.status)),
      route: active.filter(order => order.status === 'OUT_FOR_DELIVERY'),
      finished: deliveryOrders.filter(order => ['DELIVERED', 'CANCELED'].includes(order.status)).slice(0, 10),
    };
  }, [deliveryOrders]);

  useEffect(() => {
    if (grouped.newOrders.length > lastNewCount.current && lastNewCount.current !== 0) {
      const audio = new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=');
      audio.play().catch(() => undefined);
    }
    lastNewCount.current = grouped.newOrders.length;
  }, [grouped.newOrders.length]);

  const moveNext = (order: DeliveryOrder) => {
    const nextStatus = statusConfig[order.status].next;

    if (nextStatus) updateDeliveryOrderStatus(order.id, nextStatus);
  };

  const renderOrder = (order: DeliveryOrder) => (
    <article key={order.id} className="bg-white rounded-3xl border border-slate-200 shadow-sm p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{order.id}</p>
          <h3 className="text-lg font-black text-slate-900">{order.customer.name}</h3>
          <p className="text-xs text-slate-500 font-bold">{new Date(order.createdAt).toLocaleString('pt-BR')}</p>
        </div>
        <span className={`px-2.5 py-1 rounded-full border text-[10px] font-black uppercase ${statusConfig[order.status].color}`}>
          {statusConfig[order.status].label}
        </span>
      </div>

      <div className="space-y-2">
        {order.items.map(item => (
          <div key={item.id} className="flex justify-between gap-3 text-sm">
            <span className="font-bold text-slate-800">{item.quantity}x {item.productName}</span>
            <span className="font-black">{formatCurrency(item.price * item.quantity)}</span>
          </div>
        ))}
      </div>

      {order.notes && (
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3">
          <p className="text-[10px] font-black text-amber-500 uppercase">Observação</p>
          <p className="text-sm font-bold text-amber-900">{order.notes}</p>
        </div>
      )}

      <div className="bg-slate-50 rounded-2xl p-3 space-y-2 text-sm">
        <p className="font-bold flex gap-2"><Phone className="w-4 h-4 text-slate-400" /> {order.customer.phone}</p>
        <p className="font-bold flex gap-2"><MapPin className="w-4 h-4 text-slate-400" /> {getAddress(order)}</p>
        <p className="font-bold">Pagamento informado: {order.paymentMethod}{order.changeFor ? ` • Troco ${formatCurrency(Math.max(0, order.changeFor - order.total))}` : ''}</p>
        <p className={`text-xs font-black ${order.paymentStatus === 'PAID' ? 'text-emerald-700' : 'text-amber-700'}`}>{order.paymentStatus === 'PAID' ? 'Pagamento registrado no caixa' : 'Pagamento pendente'}</p>
        <p className="text-xs text-slate-500">O pagamento não altera preparo, despacho ou entrega.</p>
        {order.customer.wantsWhatsappUpdates !== false && (
          <p className="font-bold flex gap-2 text-emerald-700"><MessageCircle className="w-4 h-4" /> Cliente autorizou avisos por WhatsApp</p>
        )}
        <p className="text-lg font-black text-slate-950">Total: {formatCurrency(order.total)}</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {order.fulfillmentType === 'DELIVERY' && (
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(getAddress(order))}`} target="_blank" rel="noreferrer" className="h-11 rounded-2xl bg-slate-100 text-slate-700 font-black text-xs flex items-center justify-center gap-2">
            <MapPin className="w-4 h-4" /> Rota
          </a>
        )}
        <button type="button" disabled className="h-11 rounded-2xl bg-emerald-50 text-emerald-700 font-black text-xs flex items-center justify-center gap-2 opacity-80">
          <MessageCircle className="w-4 h-4" /> API WhatsApp pronta
        </button>
        <button onClick={() => window.print()} className="h-11 rounded-2xl bg-slate-100 text-slate-700 font-black text-xs flex items-center justify-center gap-2">
          <Printer className="w-4 h-4" /> Imprimir
        </button>
        {order.paymentStatus !== 'PAID' && order.status !== 'CANCELED' && (
          <Button onClick={() => receiveDeliveryPayment(order.id)} className="h-11 rounded-2xl text-xs bg-emerald-600 hover:bg-emerald-700">
            <CheckCircle2 className="w-4 h-4" /> Receber pagamento
          </Button>
        )}
        {statusConfig[order.status].next && (
          <Button onClick={() => moveNext(order)} className="h-11 rounded-2xl text-xs">
            <CheckCircle2 className="w-4 h-4" /> {statusConfig[order.status].action}
          </Button>
        )}
        {!['DELIVERED', 'CANCELED'].includes(order.status) && (
          <button onClick={() => updateDeliveryOrderStatus(order.id, 'CANCELED')} className="h-11 rounded-2xl bg-red-50 text-red-600 font-black text-xs flex items-center justify-center gap-2">
            <XCircle className="w-4 h-4" /> Cancelar
          </button>
        )}
      </div>
    </article>
  );

  if (currentCompany?.useDelivery !== true) {
    return (
      <div className="flex-1 bg-slate-50 overflow-y-auto p-6 flex items-center justify-center">
        <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-8 text-center max-w-md">
          <Bike className="w-14 h-14 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-slate-900">Delivery desativado</h1>
          <p className="text-sm text-slate-500 mt-2">Ative em Configurações para liberar o link público e o painel de pedidos.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-slate-50 overflow-y-auto p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="bg-gradient-to-br from-slate-950 to-brand-700 text-white rounded-[2rem] p-6 shadow-xl relative overflow-hidden">
          <Bike className="absolute right-6 top-6 w-32 h-32 text-white/10" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-[10px] font-black uppercase tracking-widest">
              <Bike className="w-4 h-4" /> Delivery próprio
            </span>
            <h1 className="text-3xl md:text-4xl font-black mt-4">Pedidos Delivery</h1>
            <p className="text-white/70 text-sm mt-2 max-w-2xl">Receba pedidos do cardápio online, acompanhe preparo, rota e deixe o fluxo pronto para automação oficial via WhatsApp API.</p>
            <a href={currentCompany ? `#/loja/${currentCompany.id}` : '#/delivery/menu'} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 bg-white text-slate-950 px-4 py-2 rounded-2xl text-sm font-black">
              Abrir cardápio público <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </header>

        <section className="grid md:grid-cols-4 gap-3">
          <div className="bg-white rounded-3xl border border-slate-200 p-4 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Fila nova</p>
            <p className="text-3xl font-black text-amber-500 mt-2">{grouped.newOrders.length}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200 p-4 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Cozinha</p>
            <p className="text-3xl font-black text-orange-500 mt-2">{grouped.preparing.length}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200 p-4 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Em rota</p>
            <p className="text-3xl font-black text-blue-500 mt-2">{grouped.route.length}</p>
          </div>
          <div className="bg-slate-950 text-white rounded-3xl p-4 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-widest text-white/50 flex items-center gap-2"><Radio className="w-4 h-4" /> WhatsApp</p>
            <p className="text-sm font-black mt-2">Estrutura pronta para conectar API oficial.</p>
          </div>
        </section>

        <div className="grid lg:grid-cols-4 gap-4">
          <section className="space-y-3">
            <h2 className="font-black flex items-center gap-2"><Clock className="w-5 h-5 text-amber-500" /> Novos ({grouped.newOrders.length})</h2>
            {grouped.newOrders.length ? grouped.newOrders.map(renderOrder) : <div className="bg-white rounded-3xl border border-dashed border-slate-200 p-8 text-center text-sm font-bold text-slate-300">Sem pedidos novos</div>}
          </section>
          <section className="space-y-3">
            <h2 className="font-black flex items-center gap-2"><PackageCheck className="w-5 h-5 text-orange-500" /> Cozinha ({grouped.preparing.length})</h2>
            {grouped.preparing.length ? grouped.preparing.map(renderOrder) : <div className="bg-white rounded-3xl border border-dashed border-slate-200 p-8 text-center text-sm font-bold text-slate-300">Nada em preparo</div>}
          </section>
          <section className="space-y-3">
            <h2 className="font-black flex items-center gap-2"><Bike className="w-5 h-5 text-blue-500" /> Em rota ({grouped.route.length})</h2>
            {grouped.route.length ? grouped.route.map(renderOrder) : <div className="bg-white rounded-3xl border border-dashed border-slate-200 p-8 text-center text-sm font-bold text-slate-300">Sem saída agora</div>}
          </section>
          <section className="space-y-3">
            <h2 className="font-black flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Finalizados</h2>
            {grouped.finished.length ? grouped.finished.map(renderOrder) : <div className="bg-white rounded-3xl border border-dashed border-slate-200 p-8 text-center text-sm font-bold text-slate-300">Histórico vazio</div>}
          </section>
        </div>
      </div>
    </div>
  );
};
